import { inferRouteObject } from 'react-router-typed-object';
import { z } from 'zod';

import { Navigate } from '@/js/libs/router';
import { PrivateRoute, Roles, or } from '@/js/libs/use-auth';

import { CompanyDetailsTab, CompanyFormSearchKey } from 'src/scenes/Administration/containers/Companies/index.ts';
import { SupportAdminTab } from 'src/scenes/Administration/containers/SupportAdmin/index.ts';
import Administration from 'src/scenes/Administration/index.ts';
import Status from 'src/scenes/Status/index.ts';

const index = true;

export const adminRouteObject = inferRouteObject({
  path: 'admin',
  element: <PrivateRoute roles={or(Roles.ADMIN, Roles.COMPANY_ADMIN)} noAccessComponent={Status.NotFound} />,
  children: [
    {
      Component: Administration.Helmet,
      children: [
        {
          path: 'users',
          children: [
            { index, Component: Administration.Users.UsersList },
            {
              path: 'create',
              Component: Administration.Users.UserForm,
            },
            {
              path: 'edit',
              children: [
                { index, element: <Navigate to='../' replace /> },
                {
                  path: ':userId',
                  Component: Administration.Users.UserForm,
                },
              ],
            },
          ],
        },
        {
          path: 'companies',
          children: [
            { index, Component: Administration.Companies.CompaniesList },
            {
              // company details and company edit stay ADMIN-only
              element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
              children: [
                {
                  path: ':companyId',
                  children: [
                    {
                      Component: Administration.Companies.CompanyDetails.Layout,
                      children: [
                        {
                          index,
                          element: <Navigate to={`./${CompanyDetailsTab.WEBSITES}`} replace />,
                        },
                        {
                          path: CompanyDetailsTab.WEBSITES,
                          Component: Administration.Companies.CompanyDetails.WebsitesList,
                        },
                        {
                          path: CompanyDetailsTab.ACCOUNTS,
                          Component: Administration.Companies.CompanyDetails.AccountsList,
                        },
                        {
                          path: CompanyDetailsTab.PROGRAMMATIC_SEATS,
                          Component: Administration.Companies.CompanyDetails.ProgrammaticSeatsList,
                        },
                        {
                          path: CompanyDetailsTab.EMAIL_SYNC,
                          element: <PrivateRoute roles={Roles.EMAIL_SYNC} noAccessComponent={Status.NotFound} />,
                          children: [
                            {
                              index,
                              Component: Administration.Companies.CompanyDetails.EmailSyncExclusionsList,
                            },
                          ],
                        },
                        {
                          path: CompanyDetailsTab.USER_GROUPS,
                          Component: Administration.Companies.CompanyDetails.UserGroupsList,
                        },
                        {
                          path: CompanyDetailsTab.PERMISSIONS,
                          Component: Administration.Companies.CompanyDetails.PermissionsList,
                        },
                      ],
                    },
                  ],
                },
                {
                  path: 'edit',
                  children: [
                    {
                      path: ':companyId',
                      Component: Administration.Companies.CompanyForm,
                      searchParams: z.object({
                        [CompanyFormSearchKey.COMPANY_DETAILS_TAB]: z.string().optional(),
                      }).parse,
                    },
                    { index, element: <Navigate to='/admin/companies' replace /> },
                    { path: '*', element: <Navigate to='/admin/companies' replace /> },
                  ],
                },
              ],
            },
          ],
        },
        {
          // every other admin section stays ADMIN-only
          element: <PrivateRoute roles={Roles.ADMIN} noAccessComponent={Status.NotFound} />,
          children: [
            {
              path: 'website-groups',
              children: [
                { index, Component: Administration.WebsiteGroups.WebsiteGroupsList },
                {
                  path: 'create',
                  Component: Administration.WebsiteGroups.WebsiteGroupForm,
                },
                {
                  path: 'edit',
                  children: [
                    {
                      path: ':websiteGroupId',
                      Component: Administration.WebsiteGroups.WebsiteGroupForm,
                    },
                    { index, element: <Navigate to='/admin/website-groups' replace /> },
                  ],
                },
              ],
            },
            { path: 'localization', Component: Administration.Localization },
            { path: 'website-mapping', Component: Administration.WebsiteMapping },
            {
              path: 'batch-k8s',
              children: [
                { index, path: 'list', Component: Administration.BatchK8s },
                { index, element: <Navigate to='/admin/batch-k8s/list' replace /> },
                { path: '*', element: <Navigate to='/admin/batch-k8s/list' replace /> },
              ],
            },
            {
              path: 'mail',
              children: [
                {
                  Component: Administration.Mail.Layout,
                  children: [
                    { path: 'console', Component: Administration.Mail.Console },
                    { path: 'sales-promotions', Component: Administration.Mail.SalesPromotions },
                  ],
                },
                { index, element: <Navigate to='./console' replace /> },
              ],
            },
            { path: 'notifications', Component: Administration.Notifications.SystemNotificationsConfiguration },
            { path: 'xandr-credentials', Component: Administration.XandrCredentials },
            { path: 'permalead', Component: Administration.Permalead },
            { path: 'xandr-segments', Component: Administration.XandrSegments },
            { path: 'publisher-catalogue', Component: Administration.PublisherCatalogue },
            {
              path: 'support',
              children: [
                {
                  Component: Administration.SupportAdmin.Layout,
                  children: [
                    { path: SupportAdminTab.CHATS, Component: Administration.SupportAdmin.ChatList },
                    { path: SupportAdminTab.KB, Component: Administration.SupportAdmin.KnowledgeBase },
                    { path: SupportAdminTab.GAPS, Component: Administration.SupportAdmin.Gaps },
                  ],
                },
                {
                  // The conversation an admin opens from the list draws its own header and its
                  // way back, so it stands beside the tabs rather than under them.
                  path: SupportAdminTab.CHATS,
                  children: [{ path: ':convId', Component: Administration.SupportAdmin.ChatOverview }],
                },
                { index, element: <Navigate to={`./${SupportAdminTab.CHATS}`} replace /> },
              ],
            },
          ],
        },
        { index, element: <Navigate to='/admin/users' replace /> },
      ],
    },
  ],
});
